// src/lib/resolveSupplierAccount.js
// Resolve a supplier partner's payable (AP) account for the "Partner → Credit → account auto-fill + grey"
// pattern used by the Purchase and Landed Cost forms (inline panels + Data Entry).

// 563 — special-purpose "holding" account roles that must never appear as a funding/payment CREDIT leg
// (e.g. 'Supplier Refund Receivable' is type=supplier but is a receivable-holding, not a payable). This is
// NOT a blanket account_role exclusion — cash/bank/equity accounts carry legitimate roles (cash, bank,
// owner_equity), so filter by role membership in this set, on TOP of the per-op type allowlist.
export const NON_PAYMENT_ROLES = new Set(['supplier_refund_holding']);

// 779 (775 row 1) — the SELL-side half of the same class. `type='client'` carries three is_system
// accounts and only ONE of them is a customer receivable:
//   Various (Receivables)  is_system=true  account_role=NULL                    <- the catch-all AR, LEGAL
//   Deferred Revenue       is_system=true  account_role='deferred_revenue'      <- holding, never an AR
//   Customer Refund Held   is_system=true  account_role='customer_refund_holding'
// Keyed on account_role, NEVER on is_system: an is_system filter would hide Various (Receivables), which
// is exactly the account the system 'Various (Customer)' partner must settle to. Same set the Sales panel
// names locally (SaleEntryPanel.jsx:33), the same set Settings ▸ Partners guards with
// (PartnersSettings.jsx:51-53) and the same set the server backstops with
// (gestion-avicole-api/server.js:18551 CLIENT_HOLDING_ROLES).
export const CLIENT_HOLDING_ROLES = new Set(['deferred_revenue', 'customer_refund_holding']);

// The ONE definition every picker reads: a special-purpose holding account, either side.
export const HOLDING_ROLES = new Set([...NON_PAYMENT_ROLES, ...CLIENT_HOLDING_ROLES]);

// 781 (fix 1) — IS THIS ACCOUNT PARTNER-BEARING? i.e. does the account itself imply WHO the partner is?
// Only the payable/receivable types do: a supplier/client account IS that partner's AP/AR, so choosing it
// names the partner. A cash/bank/equity/loan account is a place money sits — it carries no partner at all,
// so it implies NOTHING about who bought or sold, and must never be used to narrow a partner list.
// This was already the rule inside accountPartnerScope (the 'none' early return below); it is EXPORTED here
// because SaleEntryPanel had reimplemented the account->customer cross-filter locally WITHOUT the gate, so
// on a CASH sale the receipt account (Liquide/Bank) matched no customer and blanked the chosen one. One
// definition, read by both — do not re-test `a.type` anywhere else.
// This is the exact symmetry 775 row 13 already documented for the OTHER direction ("Cash returns the base
// list before the partner filter, which is correct — cash/bank accounts carry no partner").
export function isPartnerBearingAccount(account) {
  return ['supplier', 'client'].includes(String(account?.type || '').toLowerCase());
}

// True when `a` is a pickable funding/payment account for an op whose allowed credit-leg types are `allowedTypes`.
export function isPickableFunding(a, allowedTypes) {
  return !!a && !a.is_group
    && allowedTypes.includes(String(a.type || '').toLowerCase())
    && !HOLDING_ROLES.has(a.account_role);
}

// GES-202 — THE EXPENSE-ENTRY TARGET ACCOUNT, RESOLVED BY ROLE, NEVER BY DISPLAY NAME.
// Two doors, deliberately: a user-created expense account (Door 1), or THE system expense root
// (Door 2). Door 2 used to read `a.name === 'Expense'`, which is identity-by-display-name — so the
// moment prod renamed that account to "Depenses" the door shut, and because every expense account on
// prod is is_system, Door 1 was empty too: the picker went to ZERO options and expense entry stopped.
// `expense_root` is the stable identifier (measured present on that account on BOTH dev and prod).
// This predicate lived in THREE byte-identical copies (DataEntryPage x2, LinkedTransactionFields);
// it lives here once so the next rename is one fix, not three.
export const EXPENSE_ROOT_ROLE = 'expense_root';
export function isSelectableExpenseTarget(a) {
  return !!a && (!a.is_system || String(a.account_role || '') === EXPENSE_ROOT_ROLE);
}

// The system (catch-all) counterparty account for a partner side — 'client' → Various (Receivables),
// 'supplier' → Various (Payables). Resolved by the is_system flag, NEVER by name, and with the holding
// guard applied: a bare "first is_system account of this type" can land on Deferred Revenue or Customer
// Refund Held, which are also is_system + client.
export function systemAccountIdForType(accounts, type) {
  const t = String(type || '').toLowerCase();
  return (accounts || []).find(a => a.is_system === true && !a.is_group
    && String(a.type || '').toLowerCase() === t
    && !HOLDING_ROLES.has(a.account_role))?.id || null;
}

// A partner's EFFECTIVE financial account — the SAME resolution Settings ▸ Partners renders in its
// "Financial Account" column (PartnersSettings.jsx:41 `sysAcctName`), whose own comment states the rule:
// system partners carry NO stored default_account_id but post to the system counterparty account. So
//     default_account_id  ??  (is_system ? the system account of the matching side : none)
// That is what pairs 'Various (Customer)' with 'Various (Receivables)' despite a NULL default_account_id,
// and it is why filtering on the raw column alone could never work. Resolve ONCE, then the customer↔account
// cross-filter is a plain symmetric match on the resolved id, in both directions.
export function effectiveAccountIdFor(partner, accounts) {
  if (!partner) return null;
  const type = partner.type === 'Customer' ? 'client' : 'supplier';
  return partner.default_account_id
    || (partner.is_system === true ? systemAccountIdForType(accounts, type) : null);
}

// 563 (owner ruling 2026-08-04): the partner↔account pairing keys ONLY on the linked financial account
// (partner.default_account_id) — NEVER account/partner names (they can coincide) or posted history. Returns
// the partners whose linked account IS this account. length === 1 → partner-SPECIFIC (cross-filter both ways);
// 0 or >1 → POOLED, so the account legitimately serves many partners → leave the partner picker any-partner.
export function partnersLinkedToAccount(partners, accountId) {
  if (!accountId) return [];
  // A partner is linked to this account if it is ANY of the partner's account links — payable (supplier
  // side), receivable (customer side), or the generic default. Was payable-only, so a receivable account
  // (e.g. Various Receivables) could never match customers linked via preferred_receivable_account_id.
  return (partners || []).filter(p =>
    [p.preferred_payable_account_id, p.preferred_receivable_account_id, p.default_account_id].includes(accountId));
}

// ── 565: the ONE shared helper every partner+account site converges on ──────────────────────────────

// The account option list: valid types for the op + holding-role excluded + group/parent excluded.
// Feed the result to <AccountOptgroups> for the class-grouped presentation.
export function pickableAccounts(accounts, allowedTypes) {
  return (accounts || []).filter(a => isPickableFunding(a, allowedTypes));
}

// A DESIGNATED POOL account = a system supplier/client counterparty ('Various (Payables)'/'(Receivables)').
// Marked by is_system on a payable/receivable type — NEVER by name; matches FinanceForm's existing shared-account
// convention. A pool account legitimately serves ANY partner.
export function isPoolAccount(a) {
  return !!a && a.is_system === true && !HOLDING_ROLES.has(a.account_role)
    && ['supplier', 'client'].includes(String(a.type || '').toLowerCase());
}

// account → partner scope, keyed ONLY on partners.default_account_id (never names/history) + the pool marker.
// Returns { kind, partnerIds, message }:
//   'none'     — no account chosen → any partner (caller shows the full type-universe).
//   'pool'     — designated pool → any partner (partnerIds null).
//   'specific' — exactly 1 partner linked → lock to it.
//   'multi'    — >1 linked → those partners.
//   'orphaned' — 0 linked and NOT a pool → partner dropdown EMPTY + a message (owner ruling: show the account,
//                empty+message; don't hide it, don't show all).
export function accountPartnerScope(account, partners) {
  if (!account) return { kind: 'none', partnerIds: null, message: null };
  // Only supplier/client (payable/receivable) accounts are partner-bearing. A cash/bank/equity/loan account has no
  // partner association → 'none' (any partner), never 'orphaned'. 781: the test is now the exported
  // isPartnerBearingAccount above, so the Sales panel reads the SAME predicate instead of omitting it.
  const t = String(account.type || '').toLowerCase();
  if (!isPartnerBearingAccount(account)) return { kind: 'none', partnerIds: null, message: null };
  // The account returns ONLY the partners whose linked account IS this one (default_account_id) — never "all"
  // (owner 2026-08-05). A designated pool (is_system 'Various (Payables)/(Receivables)') ALSO admits its system
  // catch-all partner ('Various (Supplier)/(Customer)') so the pool returns { its pooled partners + Various }.
  let linked = partnersLinkedToAccount(partners, account.id);
  // 779: the same holding guard as the forward direction — Deferred Revenue / Customer Refund Held are
  // is_system+client too, so an unguarded is_system test would hand them the system catch-all partner.
  if (account.is_system === true && !HOLDING_ROLES.has(account.account_role)) {
    const sys = (partners || []).find(p => p.is_system && p.type === (t === 'client' ? 'Customer' : 'Supplier'));
    if (sys && !linked.some(p => p.id === sys.id)) linked = [...linked, sys];
  }
  if (linked.length === 1) return { kind: 'specific', partnerIds: [linked[0].id], message: null };
  if (linked.length > 1)  return { kind: 'multi', partnerIds: linked.map(p => p.id), message: null };
  return { kind: 'orphaned', partnerIds: [], message: 'No partner has this payment account.' };
}

// The partner id an account→partner effect should auto-set (or null for no forced change): a SPECIFIC account locks
// its one partner; a POOL account pre-fills its system counterparty when the partner is still blank (overridable).
export function autoPartnerFor(scope) {
  return scope.kind === 'specific' ? scope.partnerIds[0] : null;
}

// Given the chosen account + the type-universe of partners the caller offers (suppliers or customers), return the
// partner options to show + the scope. Orphaned → [] (caller renders scope.message). Specific → auto-select target.
export function scopedPartnerOptions(account, partners, universe) {
  const scope = accountPartnerScope(account, partners);
  if (scope.kind === 'none' || scope.kind === 'pool') return { options: universe || [], scope };
  if (scope.kind === 'orphaned') return { options: [], scope };
  const set = new Set(scope.partnerIds);
  return { options: (universe || []).filter(p => set.has(p.id)), scope };
}

// partner → account scope: the partner's linked account id(s) (default_account_id) for scoping the account list
// to a chosen partner. Pool accounts are added back by the caller (they serve any partner).
export function partnerAccountIds(partner) {
  if (!partner) return null;
  const id = partner.preferred_payable_account_id || partner.default_account_id || null;
  return id ? [id] : [];
}

// The account(s) a chosen partner may be paired with: STRICTLY its linked account (default_account_id) — NOT every
// pool. A system catch-all partner ('Various (Supplier)/(Customer)'), which has no linked account, resolves to its
// system pool account (Various Payables/Receivables). Returns the filtered account list, or null for "no partner"
// (caller shows the full list).
export function accountsForPartner(partner, accounts) {
  if (!partner) return null;
  // 779 (775 rows 1 + 15): resolve to the partner's ONE effective account (effectiveAccountIdFor — the
  // Settings ▸ Partners rule) instead of unioning "every is_system account of this type" for a system
  // partner. That union is what put Deferred Revenue and Customer Refund Held in the Sales receivable
  // picker, at index 0, where the auto-select then took them. It also drops the read of
  // `preferred_receivable_account_id` / `preferred_payable_account_id`: those columns were retired by
  // api/db/286_drop_stale_partner_preferred_accounts.sql, confirmed LIVE in 775 (a PostgREST select on
  // preferred_receivable_account_id 400s), so the read was always `undefined`.
  const linkedId = effectiveAccountIdFor(partner, accounts);
  return (accounts || []).filter(a => linkedId && a.id === linkedId);
}
//
// LINK-ONLY resolution — the partner's configured Financial Account:
//   preferred_payable_account_id → default_account_id.
// We deliberately DO NOT guess by account name (a name match could silently pick the wrong account).
// If the partner has no Financial Account set, `missing` is true → the form shows an empty account
// dropdown + a "set a Financial Account in Settings > Partners" message instead of auto-filling.
//
// Returns { accountId, locked, missing }:
//   - accountId + locked=true : the partner's configured account → auto-fill + grey the field.
//   - missing=true            : a partner is selected but has no (valid supplier) Financial Account.
export function resolveSupplierPayable(partner, accounts) {
  const supplierAccts = (accounts || []).filter(a => (a.type || '').toLowerCase() === 'supplier' && !a.is_group);
  if (!partner) return { accountId: '', locked: false, missing: false };

  const linkId = partner.preferred_payable_account_id || partner.default_account_id || '';
  if (linkId && supplierAccts.some(a => a.id === linkId)) return { accountId: linkId, locked: true, missing: false };

  // System partner ("Various" / No supplier) can't be configured in Settings by design — it posts
  // to the system supplier counterparty. Resolve to it deterministically by the is_system flag
  // (NOT a name guess): this is the designated catch-all account, so lock the field to it.
  if (partner.is_system) {
    const sys = supplierAccts.find(a => a.is_system);
    if (sys) return { accountId: sys.id, locked: true, missing: false };
  }

  return { accountId: '', locked: false, missing: true };
}
